import * as React from 'react';
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';

/**
 * Semantic accent tokens — used to color the left accent bar and icon tile.
 * Hex strings are also accepted for one-off custom colors.
 */
export type CommonCardAccent =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'info'
  | 'secondary'
  | 'highlight'
  | string;

interface CommonCardProps {
  title: string;
  /** Primary metric. Strings render as-is; numbers get thousands separators. */
  count?: string | number;
  /** Small caption shown below the metric. Falls back to `subtitle` for back-compat. */
  caption?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  /** Semantic accent — neutral by default, others map to fixed brand colors. */
  accent?: CommonCardAccent;
  children?: React.ReactNode;

  /** Legacy props — accepted for back-compat but no longer themed individually. */
  bgColor?: string;
  fontColor?: string;
}

interface AccentColors {
  bar: string;
  iconBg: string;
  iconColor: string;
}

const ACCENT_MAP: Record<string, AccentColors> = {
  neutral: { bar: '#0f172a', iconBg: 'rgba(15, 23, 42, 0.08)', iconColor: '#0f172a' },
  success: { bar: '#16a34a', iconBg: 'rgba(22, 163, 74, 0.12)', iconColor: '#15803d' },
  warning: { bar: '#d97706', iconBg: 'rgba(217, 119, 6, 0.12)', iconColor: '#b45309' },
  info: { bar: '#3482cf', iconBg: 'rgba(52, 130, 207, 0.12)', iconColor: '#1d4ed8' },
  secondary: { bar: '#7c3aed', iconBg: 'rgba(124, 58, 237, 0.12)', iconColor: '#6d28d9' },
  highlight: { bar: '#dbbe62', iconBg: 'rgba(240, 207, 105, 0.18)', iconColor: '#92400e' }
};

const resolveAccent = (accent: CommonCardAccent | undefined): AccentColors => {
  if (!accent) return ACCENT_MAP.neutral;
  if (ACCENT_MAP[accent]) return ACCENT_MAP[accent];
  // Treat as raw hex / CSS color — use the same color for bar with a derived
  // translucent tile background.
  return { bar: accent, iconBg: `${accent}1f`, iconColor: accent };
};

const formatCount = (count: CommonCardProps['count']): string => {
  if (count == null || count === '') return '';
  if (typeof count === 'number') return new Intl.NumberFormat('en-US').format(count);
  // If it parses cleanly as a number, format it; otherwise leave as-is (e.g.
  // formatted dates, "£42", percent strings).
  if (/^-?\d+(\.\d+)?$/.test(count.trim())) {
    return new Intl.NumberFormat('en-US').format(Number(count));
  }
  return count;
};

const CommonCard: React.FC<CommonCardProps> = ({
  title,
  count,
  caption,
  subtitle,
  icon,
  loading,
  accent,
  children
}) => {
  const colors = resolveAccent(accent);
  const displayCount = formatCount(count);
  const displayCaption = caption ?? subtitle;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        p: 2,
        pl: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(15, 23, 42, 0.08)',
        backgroundColor: '#ffffff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(15, 23, 42, 0.08)',
          borderColor: 'rgba(15, 23, 42, 0.12)'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          backgroundColor: colors.bar
        }
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              backgroundColor: colors.iconBg,
              color: colors.iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg': { fontSize: 22, width: 22, height: 22 }
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: 'rgba(15, 23, 42, 0.6)',
              lineHeight: 1.4
            }}
          >
            {title}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={72} sx={{ fontSize: 28, my: 0.25 }} />
          ) : (
            displayCount !== '' && (
              <Typography
                sx={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#0f172a',
                  lineHeight: 1.2,
                  mt: 0.25,
                  wordBreak: 'break-word'
                }}
              >
                {displayCount}
              </Typography>
            )
          )}
          {displayCaption && (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: 'rgba(15, 23, 42, 0.55)',
                mt: 0.25
              }}
            >
              {displayCaption}
            </Typography>
          )}
          {children && <Box sx={{ mt: 1 }}>{children}</Box>}
        </Box>
      </Stack>
    </Paper>
  );
};

export default CommonCard;
